import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react'
import {
  subscribeDayOrders,
  subscribePendingCount,
  fetchTodaysReceiptNos,
} from '../services/orders'
import { peekReceiptSeq, seedReceiptSeq } from '../services/receiptNumber'
import { useDayKey } from '../hooks/useDayKey'
import { useAuth } from './AuthContext'
import type { Order } from '../types'

interface OrdersValue {
  /** Today's orders, newest first. Only populated where `full` is requested. */
  orders: Order[]
  /** Count of orders the server has not acknowledged yet. Always live. */
  pendingCount: number
  loading: boolean
  error: string | null
}

const OrdersContext = createContext<OrdersValue | undefined>(undefined)

/**
 * The till only needs the pending-sync count, but it used to subscribe to
 * every order of the day to get it, a 500-order day meant 500 document
 * reads per device, re-delivered on every write. The full list is now loaded
 * only where it is actually shown (the day report), and the till watches a
 * cheap local-only signal instead.
 */
export function OrdersProvider({
  children,
  full = false,
  /**
   * Which day to load, 'YYYY-MM-DD'. Defaults to today; the report passes a
   * date when the cashier picks one out of the calendar.
   */
  dayKey,
}: {
  children: ReactNode
  full?: boolean
  dayKey?: string
}) {
  const { tenantId, isAuthenticated } = useAuth()
  const [orders, setOrders] = useState<Order[]>([])
  const [pendingCount, setPendingCount] = useState(0)
  // Today, kept current as the day turns, except when the report has asked
  // for a specific past day, which never moves.
  const today = useDayKey()
  const day = dayKey ?? today
  const [loading, setLoading] = useState(full)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!tenantId || !isAuthenticated) {
      setLoading(false)
      return
    }

    if (!full) {
      return subscribePendingCount(tenantId, today, setPendingCount, (err) =>
        console.error('[orders] pending', err),
      )
    }

    let seeded = false
    return subscribeDayOrders(
      tenantId,
      day,
      (items) => {
        setOrders(items)
        setPendingCount(items.filter((o) => o.pending).length)
        setLoading(false)
        // Only from today's list. Seeding the counter from an old day would
        // hand the next sale a number already printed on a receipt.
        if (!seeded && day === today) {
          seeded = true
          seedReceiptSeq(items.map((o) => o.receiptNo))
        }
      },
      (err) => {
        console.error('[orders]', err)
        setError('Siparişler yüklenemedi.')
        setLoading(false)
      },
    )
  }, [tenantId, isAuthenticated, full, day, today])

  // If localStorage was cleared mid-service the counter restarts at 1 and
  // reissues numbers already printed on receipts. Recovering costs one query,
  // so it runs only when the counter is actually blank, not on every launch.
  useEffect(() => {
    if (!tenantId || !isAuthenticated || full || peekReceiptSeq() > 0) return

    let cancelled = false
    void fetchTodaysReceiptNos(tenantId, today)
      .then((nos) => {
        if (!cancelled && nos.length) seedReceiptSeq(nos)
      })
      .catch((err) => console.error('[orders] seed', err))

    return () => {
      cancelled = true
    }
  }, [tenantId, isAuthenticated, full])

  const value = useMemo<OrdersValue>(
    () => ({ orders, pendingCount, loading, error }),
    [orders, pendingCount, loading, error],
  )

  return <OrdersContext.Provider value={value}>{children}</OrdersContext.Provider>
}

export function useOrders() {
  const ctx = useContext(OrdersContext)
  if (!ctx) throw new Error('useOrders must be used within an OrdersProvider')
  return ctx
}
